import { useState, useEffect } from "react";
import {
  getChatbotSettings,
  listKnowledgeSources,
  createKnowledgeSource,
  updateKnowledgeSource,
  deleteKnowledgeSource,
  ingestTextToSource,
  uploadSourceFile,
  ingestUploadedFile,
  listSourceChunks,
  searchPreview,
  getSourceContent,
  listSourceJobs,
} from "../../../../../features/ai_chatbots/aiChatbotsService";
import { ChatbotSettings } from "../types";

export const useChatbotTraining = (
  chatbotId: number | undefined,
  setSettingsData: React.Dispatch<React.SetStateAction<ChatbotSettings>>
) => {
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [sources, setSources] = useState<any[]>([]);
  const [sourceStatsMap, setSourceStatsMap] = useState<
    Record<number, { chunks: number; chars: number; tokensEst: number }>
  >({});

  // Source management states
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [newSourceTitle, setNewSourceTitle] = useState("");
  const [newSourceLang, setNewSourceLang] = useState("en");
  const [newText, setNewText] = useState("");
  const [contentType, setContentType] = useState<"text" | "file">("text");
  const [fileUploading, setFileUploading] = useState(false);

  // Search states
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Polling states
  const [jobs, setJobs] = useState<any[]>([]);
  const [polling, setPolling] = useState(false);

  const loadSettings = async () => {
    if (!chatbotId) return;
    setTrainingLoading(true);
    setTrainingError(null);
    try {
      const s = await getChatbotSettings(chatbotId);
      if (s && Object.keys(s).length > 0) {
        setSettingsData((prev: any) => ({
          ...prev,
          ...s,
          // Deep merge ensuring nested objects exist
          assistant_profile: { ...prev.assistant_profile, ...s.assistant_profile },
          retrieval: { ...prev.retrieval, ...s.retrieval },
          guardrails: { ...prev.guardrails, ...s.guardrails },
          limits: { ...prev.limits, ...s.limits },
          provider: { ...prev.provider, ...s.provider },
        }));
      }
    } catch (e: any) {
      setTrainingError(e?.message || "Failed to load settings");
    } finally {
      setTrainingLoading(false);
    }
  };

  const refreshSources = async () => {
    if (!chatbotId) return;
    setTrainingError(null);
    try {
      const srcs = await listKnowledgeSources(chatbotId);
      setSources(srcs);

      // Build stats map from latest_job.stats.chunks_created
      const statsEntries: [number, { chunks: number; chars: number; tokensEst: number }][] = [];
      for (const src of srcs) {
        const chunks = src.latest_job?.stats?.chunks_created || 0;
        if (chunks > 0) {
          // Rough estimates since we don't fetch actual chunk data anymore
          const estimatedChars = chunks * 500; // average 500 chars per chunk
          const estimatedTokens = Math.round(estimatedChars / 4); // ~4 chars per token
          statsEntries.push([src.id, { chunks, chars: estimatedChars, tokensEst: estimatedTokens }]);
        }
      }
      if (statsEntries.length) {
        setSourceStatsMap(Object.fromEntries(statsEntries));
      }
    } catch (e: any) {
      setTrainingError(e?.message || "Failed to load sources");
    }
  };

  useEffect(() => {
    if (chatbotId) {
      loadSettings();
    }
  }, [chatbotId, setSettingsData]);

  // Job polling logic
  useEffect(() => {
    let timer: any;
    const poll = async () => {
      if (!chatbotId || !selectedSourceId) return;
      try {
        const list = await listSourceJobs(chatbotId, selectedSourceId, { limit: 5 });
        setJobs(list);
        const hasRunning = list.some((j: any) => j.status === "queued" || j.status === "running");
        if (hasRunning) {
          timer = setTimeout(poll, 1500);
          setPolling(true);
        } else {
          setPolling(false);
        }
      } catch { }
    };
    poll();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [chatbotId, selectedSourceId]);

  // Refresh source content on job success
  useEffect(() => {
    const refreshOnSuccess = async () => {
      if (!chatbotId || !selectedSourceId || !jobs?.length) return;
      const succeeded = jobs.find((j: any) => j.status === "succeeded");
      if (!succeeded) return;
      try {
        const content = await getSourceContent(chatbotId, selectedSourceId);
        setNewText(content.text || "");
        setSources((prev) =>
          prev.map((p) =>
            p.id === selectedSourceId
              ? { ...p, version: content.version, updated_at: new Date().toISOString() }
              : p
          )
        );
        const fullText = content.text || "";
        setSourceStatsMap((prev) => ({
          ...prev,
          [selectedSourceId]: {
            chunks: content.chunks || 0,
            chars: fullText.length,
            tokensEst: Math.ceil(fullText.length / 4),
          },
        }));
      } catch { }
    };
    refreshOnSuccess();
  }, [jobs, chatbotId, selectedSourceId]);

  const handleCreateSource = async (params?: { title: string; lang: string; type: string }) => {
    const title = params?.title ?? newSourceTitle;
    const lang = params?.lang ?? newSourceLang;
    const type = params?.type ?? contentType;

    if (!chatbotId || !title.trim()) return null;
    try {
      const created = await createKnowledgeSource(chatbotId, {
        type: type === "file" ? "file" : "text",
        title: title.trim(),
        lang: lang,
      });
      setSources([created, ...sources]);
      if (!params) setNewSourceTitle("");
      setSelectedSourceId(created.id);
      return created;
    } catch (error) {
      console.error("Failed to create source:", error);
      return null;
    }
  };

  const handleIngestText = async (sourceId?: number, text?: string) => {
    const targetSourceId = sourceId ?? selectedSourceId;
    const targetText = text ?? newText;

    if (!chatbotId || !targetSourceId || !targetText.trim()) return;
    await ingestTextToSource(chatbotId, targetSourceId, targetText);
    if (!sourceId) setNewText("");
    // Trigger polling or refresh via jobs
  };

  const handleSearchPreview = async () => {
    if (!chatbotId || !searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await searchPreview(chatbotId, searchQ.trim());
      setSearchResults(res);
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  const handleFileUpload = async (file: File, sourceId: number) => {
    if (!chatbotId) return;
    setFileUploading(true);
    try {
      const uploadRes = await uploadSourceFile(chatbotId, sourceId, file);
      if (uploadRes?.upload_id) {
        await ingestUploadedFile(chatbotId, sourceId, uploadRes.upload_id);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      setTrainingError("File upload failed");
    } finally {
      setFileUploading(false);
    }
  };

  const handleDeleteSource = async (sourceId: number) => {
    if (!chatbotId) return;
    try {
      await deleteKnowledgeSource(chatbotId, sourceId);
      setSources((prev) => prev.filter((s) => s.id !== sourceId));
      if (selectedSourceId === sourceId) {
        setSelectedSourceId(null);
      }
    } catch (error) {
      console.error("Delete source failed:", error);
    }
  };

  return {
    trainingLoading,
    trainingError,
    sources,
    setSources, // Exposed for manual updates if needed
    sourceStatsMap,
    selectedSourceId,
    setSelectedSourceId,
    newSourceTitle,
    setNewSourceTitle,
    newSourceLang,
    setNewSourceLang,
    newText,
    setNewText,
    contentType,
    setContentType,
    fileUploading,
    searchQ,
    setSearchQ,
    searchResults,
    jobs,
    polling,
    handleCreateSource,
    handleIngestText,
    handleFileUpload,
    handleDeleteSource,
    handleSearchPreview,
    refreshSources,
  };
};
